import type {
  FishProductId,
  FishShopV2State,
  FishShopV2ViewerPlayer,
  FishShopV2ViewerState,
  MaskedFishRoundResult,
} from "../types";
import { FISH_PRODUCT_IDS } from "../types";

function maskResult(result: {
  readonly productId: MaskedFishRoundResult["productId"];
  readonly soldTicketCount: number;
  readonly finalPriceWon: MaskedFishRoundResult["finalPriceWon"];
  readonly totalRevenueWon: MaskedFishRoundResult["totalRevenueWon"];
  readonly winnerIds: MaskedFishRoundResult["winnerIds"];
  readonly payoutByPlayerId: MaskedFishRoundResult["payoutByPlayerId"];
  readonly hidden: boolean;
}): MaskedFishRoundResult {
  if (!result.hidden) {
    return result;
  }

  return {
    productId: result.productId,
    soldTicketCount: result.soldTicketCount,
    finalPriceWon: null,
    totalRevenueWon: null,
    winnerIds: null,
    payoutByPlayerId: null,
    hidden: true,
  };
}

function projectPlayer(
  state: FishShopV2State,
  viewerId: string,
  playerId: string,
): FishShopV2ViewerPlayer {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }

  const isSelf = player.playerId === viewerId;
  const isCompleted = state.status === "COMPLETED";

  return {
    playerId: player.playerId,
    remainingTickets: isSelf ? player.remainingTickets : null,
    totalIncomeWon: isCompleted ? player.totalIncomeWon : null,
    itemStatus: isSelf ? player.itemStatus : null,
    checkSnapshotWon: isSelf ? player.checkSnapshotWon : null,
  };
}

function getPriceSubmitterIds(state: FishShopV2State): readonly string[] {
  const ids = new Set<string>();
  for (const productId of FISH_PRODUCT_IDS) {
    for (const seller of state.currentRound.market[productId].sellers) {
      ids.add(seller.playerId);
    }
  }
  return [...ids];
}

function isKnownFishProductId(productId: string): productId is FishProductId {
  return FISH_PRODUCT_IDS.includes(productId as FishProductId);
}

function getPriceSubmittedPlayerIds(state: FishShopV2State): readonly string[] {
  return Object.entries(state.currentRound.priceSubmissions)
    .filter(([, prices]) =>
      Object.keys(prices).every((productId) => isKnownFishProductId(productId)),
    )
    .map(([playerId]) => playerId);
}

export function filterStateForViewer(
  state: FishShopV2State,
  viewerId: string,
): FishShopV2ViewerState {
  const ownAllocation = state.currentRound.allocations[viewerId] ?? null;
  const ownPriceSubmission =
    state.currentRound.priceSubmissions[viewerId] ?? null;

  return {
    gameId: state.gameId,
    status: state.status,
    roundCount: state.roundCount,
    currentRound: {
      roundNumber: state.currentRound.roundNumber,
      phase: state.currentRound.phase,
      allocatedPlayerIds: Object.keys(state.currentRound.allocations),
      ownAllocation,
      market: state.currentRound.market,
      priceSubmitterIds: getPriceSubmitterIds(state),
      priceSubmittedPlayerIds: getPriceSubmittedPlayerIds(state),
      ownPriceSubmission,
      productResults: state.currentRound.productResults.map(maskResult),
      confirmedPlayerIds: state.currentRound.confirmedPlayerIds,
    },
    players: state.players.map((player) =>
      projectPlayer(state, viewerId, player.playerId),
    ),
    winnerIds: state.winnerIds,
    lowestPlayerIds: state.lowestPlayerIds,
    garnetAwardsByPlayerId: state.garnetAwardsByPlayerId,
    restartNumber: state.restartNumber,
    hostPlayerId: state.hostPlayerId,
  };
}
